import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { getAgentSession, isUserAuthenticated, logout as logoutUser } from '../components/verification/verificationApi';
import { AGENT_SESSION_CLEARED } from '../services/agentSession';

interface AuthContextType {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => isUserAuthenticated());

  useEffect(() => {
    const onSessionCleared = () => setIsAuthenticated(false);
    window.addEventListener(AGENT_SESSION_CLEARED, onSessionCleared);
    return () => window.removeEventListener(AGENT_SESSION_CLEARED, onSessionCleared);
  }, []);

  const logout = useCallback(() => {
    logoutUser();
    setIsAuthenticated(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const session = getAgentSession();
    if (!session) {
      logout();
      return;
    }
    if (!Number.isFinite(session.expiresAt)) return;
    const timeout = window.setTimeout(logout, Math.max(0, session.expiresAt - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [isAuthenticated, logout]);

  const value = React.useMemo(() => ({
    isAuthenticated,
    setIsAuthenticated,
    logout
  }), [isAuthenticated, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
